import React, { useEffect, useState } from 'react';
import { LiaCheckCircle } from 'react-icons/lia';
import { FiInfo } from 'react-icons/fi';

const formatNumber = (num) => {
  const number = typeof num === 'string' ? parseFloat(num) : num;
  return number.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const PaymentAmenities = () => {
  const [bookingData, setBookingData] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [hotelProperty, setHotelProperty] = useState(null);
  const [baseAmount, setBaseAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [cgstAmount, setCgstAmount] = useState(0);
  const [sgstAmount, setSgstAmount] = useState(0);

  useEffect(() => {
    const storedBookingData = JSON.parse(localStorage.getItem('bookingDetails'));
    const storedBillingData = JSON.parse(localStorage.getItem('billingDetails'));
    const storedHotelProperty = JSON.parse(localStorage.getItem('hotelPropertyCache'));

    if (storedBillingData) {
      const finalAmount = storedBillingData.finalamount;
      const baseAmount = (finalAmount / 1.12).toFixed(2);
      const taxAmount = (finalAmount - baseAmount).toFixed(2);
      const cgstAmount = (taxAmount / 2).toFixed(2);
      const sgstAmount = (taxAmount / 2).toFixed(2);

      setFinalAmount(finalAmount);
      setBaseAmount(baseAmount);
      setTaxAmount(taxAmount);
      setCgstAmount(cgstAmount);
      setSgstAmount(sgstAmount);
      setBillingDetails(storedBillingData);
    }

    if (storedBookingData) {
      const primaryBooking = storedBookingData.primaryBooking || storedBookingData.bookings[0];
      setBookingData({
        checkindate: primaryBooking.checkin_date,
        checkoutdate: primaryBooking.checkout_date,
        roomtypename: primaryBooking.room_type.name,
        numberofguests: primaryBooking.number_of_guests,
        firstname: primaryBooking.guest_name.split(' ')[0],
        lastname: primaryBooking.guest_name.split(' ').slice(1).join(' '),
        phonenumber: primaryBooking.guest_phone,
        emailid: primaryBooking.guest_email,
        booking_type: primaryBooking.booking_status === 'Hard' ? 'Prepaid' : 'Regular',
      });
    }

    if (storedHotelProperty) {
      setHotelProperty(storedHotelProperty.data);
    }
  }, []);

  const handleDone = () => {
    localStorage.removeItem('billingDetails');
    window.location.href = 'reservation/options/booking/welcome';
  };

  if (!bookingData || !billingDetails || !hotelProperty) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="text-gray-700 font-medium">Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
          {/* Success Header - Enhanced */}
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full"></div>
            </div>
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6 shadow-lg">
                <LiaCheckCircle className="text-black w-12 h-12" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-3 tracking-tight">
                Amenities Payment Successful!
              </h2>
              <p className="text-emerald-50 text-lg max-w-2xl mx-auto">
                Your amenities have been booked successfully. Enjoy your experience!
              </p>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
            {/* Left Column - Booking Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Booking Summary Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full mr-2"></span>
                    Booking Summary
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Check-in Date
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(bookingData.checkindate)}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Number of Guests
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {bookingData.numberofguests}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Check-out Date
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(bookingData.checkoutdate)}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                          Room Type
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {bookingData.roomtypename}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest Information Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full mr-2"></span>
                    Guest Information
                  </h3>
                </div>
                <div className="p-6">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-5 mb-4">
                    <p className="text-xs uppercase tracking-wider text-indigo-600 mb-1">
                      Full Name
                    </p>
                    <p className="text-xl font-semibold text-gray-900 capitalize">
                      {bookingData.firstname} {bookingData.lastname}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                        Contact Number
                      </p>
                      <p className="text-lg font-medium text-gray-900">{bookingData.phonenumber}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Email</p>
                      <p className="text-lg font-medium text-gray-900 break-all">
                        {bookingData.emailid}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Payment Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 sticky top-8 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-6 pb-3 border-b border-gray-700">
                  Payment Summary
                </h3>

                <div className="space-y-4">
                  <div className="flex justify-between items-center text-gray-300">
                    <span>Subtotal</span>
                    <span className="font-medium text-white">₹{formatNumber(baseAmount)}</span>
                  </div>

                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-300">Taxes</span>
                      <div className="group relative inline-block">
                        <FiInfo className="text-gray-400 hover:text-white cursor-pointer transition-colors" />
                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-56 bg-white rounded-xl shadow-2xl p-4 border border-gray-200 z-10">
                          <div className="text-sm space-y-2">
                            <div className="flex justify-between text-gray-700">
                              <span>CGST (6%)</span>
                              <span className="font-medium">₹{formatNumber(cgstAmount)}</span>
                            </div>
                            <div className="flex justify-between text-gray-700">
                              <span>SGST (6%)</span>
                              <span className="font-medium">₹{formatNumber(sgstAmount)}</span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-gray-200 text-xs text-gray-500">
                              Total Tax: ₹{formatNumber(taxAmount)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium text-white">₹{formatNumber(taxAmount)}</span>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">Total Amount</span>
                      <span className="text-3xl font-bold text-emerald-400">
                        ₹{formatNumber(finalAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 space-y-3">
                  <button
                    onClick={handleDone}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl"
                  >
                    Continue to Welcome
                  </button>

                  {hotelProperty?.phone && (
                    <div className="text-center mt-4 p-3 bg-gray-800 rounded-lg">
                      <p className="text-xs text-gray-400">Need assistance?</p>
                      <p className="text-sm font-medium text-emerald-400">{hotelProperty.phone}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 text-center border-t border-gray-700">
            {hotelProperty?.name && (
              <p className="text-emerald-400 font-medium mb-2">{hotelProperty.name}</p>
            )}
            {hotelProperty?.address && (
              <p className="text-gray-400 text-sm mb-3">{hotelProperty.address}</p>
            )}
            <p className="text-gray-400">
              Thank you for choosing our amenities. We hope you have a wonderful stay!
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentAmenities;

// import React, { useEffect, useState } from "react";
// import qr from "../../assets/QR.png";
// import { LiaCheckCircle } from "react-icons/lia";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack, IoMdPrint } from "react-icons/io";
// import { FiInfo } from "react-icons/fi";

// const PaymentAmenities = () => {
//   const [bookingData, setBookingData] = useState(null);
//   const [billingDetails, setBillingDetails] = useState(null);
//   const [hotelProperty, setHotelProperty] = useState(null);
//   const [baseAmount, setBaseAmount] = useState(0);
//   const [taxAmount, setTaxAmount] = useState(0);
//   const [finalAmount, setFinalAmount] = useState(0);
//   const [showTaxTooltip, setShowTaxTooltip] = useState(false);

//   // Function to format date as dd-mm-yyyy
//   const formatDate = (dateString) => {
//     if (!dateString) return "";

//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return dateString; // Return original if invalid date

//     const day = String(date.getDate()).padStart(2, '0');
//     const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
//     const year = date.getFullYear();

//     return `${day}-${month}-${year}`;
//   };

//   useEffect(() => {
//     const storedBookingData = JSON.parse(
//       localStorage.getItem("bookingDetails")
//     );
//     const storedBillingData = JSON.parse(
//       localStorage.getItem("billingDetails")
//     );
//     const storedHotelProperty = JSON.parse(
//       localStorage.getItem("hotelPropertyCache")
//     );

//     if (storedBillingData) {
//       const finalAmount = storedBillingData.finalamount;
//       const baseAmount = (finalAmount / 1.12).toFixed(2);
//       const taxAmount = (finalAmount - baseAmount).toFixed(2);

//       setFinalAmount(finalAmount);
//       setBaseAmount(baseAmount);
//       setTaxAmount(taxAmount);
//       setBillingDetails(storedBillingData);
//     }

//     if (storedBookingData) {
//       const primaryBooking =
//         storedBookingData.primaryBooking || storedBookingData.bookings[0];
//       setBookingData({
//         checkindate: primaryBooking.checkin_date,
//         checkoutdate: primaryBooking.checkout_date,
//         roomtypename: primaryBooking.room_type.name,
//         numberofguests: primaryBooking.number_of_guests,
//         firstname: primaryBooking.guest_name.split(" ")[0],
//         lastname: primaryBooking.guest_name.split(" ").slice(1).join(" "),
//         phonenumber: primaryBooking.guest_phone,
//         emailid: primaryBooking.guest_email,
//         booking_type:
//           primaryBooking.booking_status === "Hard" ? "Prepaid" : "Regular",
//       });
//     }

//     if (storedHotelProperty) {
//       setHotelProperty(storedHotelProperty.data);
//     }
//   }, []);

//   const handleDone = () => {
//     // Remove billingDetails from localStorage
//     localStorage.removeItem("billingDetails");
//     window.location.href = "reservation/options/booking/welcome";
//   };

//   const handlePrint = () => {
//     window.print();
//   };

//   if (!bookingData || !billingDetails || !hotelProperty) {
//     return (
//       <div className="flex items-center justify-center h-screen text-gray-700">
//         Loading booking details...
//       </div>
//     );
//   }

//   // Calculate CGST and SGST (6% each)
//   const cgstAmount = (taxAmount / 2).toFixed(2);
//   const sgstAmount = (taxAmount / 2).toFixed(2);

//   return (
//     <div className="min-h-screen bg-gray-50">
//       {/* Main Content */}
//       <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
//         <div className="bg-white shadow-xl rounded-lg overflow-hidden">
//           {/* Success Banner */}
//           <div className="bg-gray-100 border-l-4 border-black p-6 text-center relative">

//             <LiaCheckCircle className="text-green-400 w-14 h-14 mb-4 mx-auto" />
//             <h2 className="text-3xl font-bold mb-2">
//               Amenities Payment Successful
//             </h2>
//             <p className="text-black">
//               Your amenities have been booked successfully Enjoy your
//               experience!
//             </p>
//           </div>

//           {/* Content Grid */}
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
//             {/* Left Column - Booking Details */}
//             <div className="lg:col-span-2 space-y-8">
//               {/* Booking Summary */}
//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Booking Summary
//                 </h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-in Date</p>
//                       <p className="font-medium">{formatDate(bookingData.checkindate)}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Number of Guests</p>
//                       <p className="font-medium">
//                         {bookingData.numberofguests}
//                       </p>
//                     </div>
//                   </div>
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-out Date</p>
//                       <p className="font-medium">{formatDate(bookingData.checkoutdate)}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Personal Details */}
//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Guest Information
//                 </h3>
//                 <div className="space-y-4">
//                   <div>
//                     <p className="text-sm text-gray-500">Full Name</p>
//                     <p className="font-medium text-transform: capitalize">
//                       {bookingData.firstname} {bookingData.lastname}
//                     </p>
//                   </div>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <p className="text-sm text-gray-500">Contact Number</p>
//                       <p className="font-medium">{bookingData.phonenumber}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Email</p>
//                       <p className="font-medium">{bookingData.emailid}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Right Column - Payment Summary */}
//             <div className="bg-gray-50 rounded-lg p-6 h-fit sticky top-8">
//               <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                 Payment Summary
//               </h3>
//               <div className="space-y-4">
//                 <div className="flex justify-between">
//                   <span className="text-gray-600">Subtotal:</span>
//                   <span className="font-medium">
//                     ₹
//                     {Number(baseAmount).toLocaleString("en-IN", {
//                       minimumFractionDigits: 2,
//                     })}
//                   </span>
//                 </div>
//                 <div className="flex justify-between relative">
//                   <div className="flex items-center">
//                     <span className="text-gray-600 mr-1">Taxes</span>
//                     <div
//                       onMouseEnter={() => setShowTaxTooltip(true)}
//                       onMouseLeave={() => setShowTaxTooltip(false)}
//                       className="cursor-pointer"
//                     >
//                       <FiInfo className="text-gray-400 hover:text-gray-600" />
//                     </div>
//                   </div>
//                   <span className="font-medium">
//                     ₹
//                     {Number(taxAmount).toLocaleString("en-IN", {
//                       minimumFractionDigits: 2,
//                     })}
//                   </span>

//                   {/* Tax Breakdown Tooltip */}
//                   {showTaxTooltip && (
//                     <div className="absolute left-0 top-full mt-1 w-1/2 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-10">
//                       <div className="flex justify-between text-sm">
//                         <span>CGST (6%)</span>
//                         <span>₹{cgstAmount}</span>
//                       </div>
//                       <div className="flex justify-between text-sm mt-1">
//                         <span>SGST (6%)</span>
//                         <span>₹{sgstAmount}</span>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//                 <div className="pt-4 border-t border-gray-200">
//                   <div className="flex justify-between items-center">
//                     <span className="font-semibold">Total:</span>
//                     <span className="text-2xl font-bold">
//                       ₹
//                       {Number(finalAmount).toLocaleString("en-IN", {
//                         minimumFractionDigits: 2,
//                       })}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div className="mt-8 space-y-4">
//                 <button
//                   onClick={handleDone}
//                   className="w-full flex items-center justify-center bg-white border border-black text-black py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
//                 >
//                   Continue to Welcome
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* Footer Note - Updated with Hotel Property Details */}
//           <div className="bg-gray-100 p-6 text-center border-t border-gray-200">
//             <p className="text-gray-600">
//               Need assistance? Contact our concierge at {hotelProperty.email || "amenities@grandhotel.com"}
//             </p>
//             <p className="text-gray-600 mt-1">
//               Call us at {hotelProperty.phone || "+91 8698732336"} for immediate assistance
//             </p>
//             <p className="text-gray-600 mt-2 text-sm">
//               {hotelProperty.name} - {hotelProperty.address}
//             </p>
//             <p className="text-gray-600 mt-1">
//               Enjoy your premium amenities during your stay!
//             </p>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default PaymentAmenities;

// import React, { useEffect, useState } from "react";
// import qr from "../../assets/QR.png";
// import { LiaCheckCircle } from "react-icons/lia";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack, IoMdPrint } from "react-icons/io";
// import { FiInfo } from "react-icons/fi";

// const PaymentAmenities = () => {
//   const [bookingData, setBookingData] = useState(null);
//   const [billingDetails, setBillingDetails] = useState(null);
//   const [baseAmount, setBaseAmount] = useState(0);
//   const [taxAmount, setTaxAmount] = useState(0);
//   const [finalAmount, setFinalAmount] = useState(0);
//   const [showTaxTooltip, setShowTaxTooltip] = useState(false);

//   // Function to format date as dd-mm-yyyy
//   const formatDate = (dateString) => {
//     if (!dateString) return "";

//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return dateString; // Return original if invalid date

//     const day = String(date.getDate()).padStart(2, '0');
//     const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
//     const year = date.getFullYear();

//     return `${day}-${month}-${year}`;
//   };

//   useEffect(() => {
//     const storedBookingData = JSON.parse(
//       localStorage.getItem("bookingDetails")
//     );
//     const storedBillingData = JSON.parse(
//       localStorage.getItem("billingDetails")
//     );

//     if (storedBillingData) {
//       const finalAmount = storedBillingData.finalamount;
//       const baseAmount = (finalAmount / 1.12).toFixed(2);
//       const taxAmount = (finalAmount - baseAmount).toFixed(2);

//       setFinalAmount(finalAmount);
//       setBaseAmount(baseAmount);
//       setTaxAmount(taxAmount);
//       setBillingDetails(storedBillingData);
//     }

//     if (storedBookingData) {
//       const primaryBooking =
//         storedBookingData.primaryBooking || storedBookingData.bookings[0];
//       setBookingData({
//         checkindate: primaryBooking.checkin_date,
//         checkoutdate: primaryBooking.checkout_date,
//         roomtypename: primaryBooking.room_type.name,
//         numberofguests: primaryBooking.number_of_guests,
//         firstname: primaryBooking.guest_name.split(" ")[0],
//         lastname: primaryBooking.guest_name.split(" ").slice(1).join(" "),
//         phonenumber: primaryBooking.guest_phone,
//         emailid: primaryBooking.guest_email,
//         booking_type:
//           primaryBooking.booking_status === "Hard" ? "Prepaid" : "Regular",
//       });
//     }
//   }, []);

//   const handleDone = () => {
//     window.location.href = "reservation/options/booking/welcome";
//   };

//   const handlePrint = () => {
//     window.print();
//   };

//   if (!bookingData || !billingDetails) {
//     return (
//       <div className="flex items-center justify-center h-screen text-gray-700">
//         Loading booking details...
//       </div>
//     );
//   }

//   // Calculate CGST and SGST (6% each)
//   const cgstAmount = (taxAmount / 2).toFixed(2);
//   const sgstAmount = (taxAmount / 2).toFixed(2);

//   return (
//     <div className="min-h-screen bg-gray-50">
//       {/* Main Content */}
//       <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
//         <div className="bg-white shadow-xl rounded-lg overflow-hidden">
//           {/* Success Banner */}
//           <div className="bg-gray-100 border-l-4 border-black p-6 text-center relative">

//             <LiaCheckCircle className="text-green-400 w-14 h-14 mb-4 mx-auto" />
//             <h2 className="text-3xl font-bold mb-2">
//               Amenities Payment Successful
//             </h2>
//             <p className="text-black">
//               Your amenities have been booked successfully Enjoy your
//               experience!
//             </p>
//           </div>

//           {/* Content Grid */}
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
//             {/* Left Column - Booking Details */}
//             <div className="lg:col-span-2 space-y-8">
//               {/* Booking Summary */}
//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Booking Summary
//                 </h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-in Date</p>
//                       <p className="font-medium">{formatDate(bookingData.checkindate)}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Number of Guests</p>
//                       <p className="font-medium">
//                         {bookingData.numberofguests}
//                       </p>
//                     </div>
//                   </div>
//                   <div className="space-y-4">
//                     <div>
//                       <p className="text-sm text-gray-500">Check-out Date</p>
//                       <p className="font-medium">{formatDate(bookingData.checkoutdate)}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Personal Details */}
//               <div className="border border-gray-200 rounded-lg p-6">
//                 <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                   Guest Information
//                 </h3>
//                 <div className="space-y-4">
//                   <div>
//                     <p className="text-sm text-gray-500">Full Name</p>
//                     <p className="font-medium text-transform: capitalize">
//                       {bookingData.firstname} {bookingData.lastname}
//                     </p>
//                   </div>
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <p className="text-sm text-gray-500">Contact Number</p>
//                       <p className="font-medium">{bookingData.phonenumber}</p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-500">Email</p>
//                       <p className="font-medium">{bookingData.emailid}</p>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Right Column - Payment Summary */}
//             <div className="bg-gray-50 rounded-lg p-6 h-fit sticky top-8">
//               <h3 className="text-xl font-bold mb-6 pb-2 border-b border-gray-200">
//                 Payment Summary
//               </h3>
//               <div className="space-y-4">
//                 <div className="flex justify-between">
//                   <span className="text-gray-600">Subtotal:</span>
//                   <span className="font-medium">
//                     ₹
//                     {Number(baseAmount).toLocaleString("en-IN", {
//                       minimumFractionDigits: 2,
//                     })}
//                   </span>
//                 </div>
//                 <div className="flex justify-between relative">
//                   <div className="flex items-center">
//                     <span className="text-gray-600 mr-1">Taxes</span>
//                     <div
//                       onMouseEnter={() => setShowTaxTooltip(true)}
//                       onMouseLeave={() => setShowTaxTooltip(false)}
//                       className="cursor-pointer"
//                     >
//                       <FiInfo className="text-gray-400 hover:text-gray-600" />
//                     </div>
//                   </div>
//                   <span className="font-medium">
//                     ₹
//                     {Number(taxAmount).toLocaleString("en-IN", {
//                       minimumFractionDigits: 2,
//                     })}
//                   </span>

//                   {/* Tax Breakdown Tooltip */}
//                   {showTaxTooltip && (
//                     <div className="absolute left-0 top-full mt-1 w-1/2 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-10">
//                       <div className="flex justify-between text-sm">
//                         <span>CGST (6%)</span>
//                         <span>₹{cgstAmount}</span>
//                       </div>
//                       <div className="flex justify-between text-sm mt-1">
//                         <span>SGST (6%)</span>
//                         <span>₹{sgstAmount}</span>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//                 <div className="pt-4 border-t border-gray-200">
//                   <div className="flex justify-between items-center">
//                     <span className="font-semibold">Total:</span>
//                     <span className="text-2xl font-bold">
//                       ₹
//                       {Number(finalAmount).toLocaleString("en-IN", {
//                         minimumFractionDigits: 2,
//                       })}
//                     </span>
//                   </div>
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div className="mt-8 space-y-4">
//                 <button
//                   onClick={handleDone}
//                   className="w-full flex items-center justify-center bg-white border border-black text-black py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
//                 >
//                   Continue to Welcome
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* Footer Note */}
//           <div className="bg-gray-100 p-6 text-center border-t border-gray-200">
//             <p className="text-gray-600">
//               Need assistance? Contact our concierge at amenities@grandhotel.com
//             </p>
//             <p className="text-gray-600 mt-1">
//               Enjoy your premium amenities during your stay!
//             </p>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default PaymentAmenities;
